/**
 * Why a scan run did not land (2026-10-15 owner ask: the scanner is the top
 * suspected reason trainers delete their data, and the admin could only
 * count the failed runs).
 *
 * Every live scan run and every screenshot batch already reports one
 * scan_perf row with a success flag (lib/scanRunReport). From this change a
 * run that wrote nothing also says WHY, in one of these words. The KEYS
 * mirror SCAN_OUTCOMES in cloudflare/src/lib/scanOutcome.ts (the Worker
 * cannot import from the client); cloudflare/src/lib/scanOutcome.test.ts
 * fails if the two lists drift. The labels live here only.
 *
 * The decision is made on the phone, from signals the hook already tracks for
 * the HUD's coaching: nothing new is read, nothing is sent that was not sent
 * before except the word.
 */

export const SCAN_OUTCOMES = ["no_text", "wrong_screen", "timeout", "cancelled", "nothing_new", "unknown"] as const;
export type ScanOutcome = (typeof SCAN_OUTCOMES)[number];

/** The admin's tally key for failed runs from builds that send no word. */
export type ScanOutcomeKey = ScanOutcome | "unreported";

/** The admin's short name for each, for tallies and table cells. */
export const SCAN_OUTCOME_SHORT: Record<ScanOutcomeKey, string> = {
  no_text: "Nothing readable",
  wrong_screen: "Not on the Pokédex",
  timeout: "OCR timed out",
  cancelled: "Stopped early",
  nothing_new: "Nothing new to add",
  unknown: "Could not tell",
  unreported: "Older build, no reason",
};

/** The longer reading, for hover text and the Leaving page. */
export const SCAN_OUTCOME_NOTE: Record<ScanOutcomeKey, string> = {
  no_text: "The OCR read nothing usable on any frame: a black screen, a blur, or the camera never showed GO.",
  wrong_screen: "Frames had text but the Pokédex (or a Pokémon page) was never on screen: the map, the menu, another app.",
  timeout: "Most OCR calls hit their deadline: the phone was too slow for the scanner.",
  cancelled: "Stopped within seconds, or restarted before anything could land.",
  nothing_new: "Cells were resolved on the Pokédex but every one was already in the dex: the scanner worked, there was nothing to write.",
  unknown: "The build looked at the run and could not tell which of these it was.",
  unreported: "A build from before the outcome field: the run failed, the reason was never recorded.",
};

/** A reason the scanner itself is to blame for, as opposed to the trainer or the dex. */
export function isScannerFault(k: ScanOutcomeKey): boolean {
  return k === "no_text" || k === "timeout";
}

export function isScanOutcome(v: unknown): v is ScanOutcome {
  return typeof v === "string" && (SCAN_OUTCOMES as readonly string[]).includes(v);
}

/**
 * What the live scan hook knows about a run when it ends. Every field is a
 * value the hook already tracks for the HUD; see useDexLiveScan's stop().
 */
export interface LiveScanSignals {
  /** Wall-clock ms from the first timed frame to the last. */
  wallMs: number;
  /** Numbers the OCR read across the run (any screen, timers included). */
  numbersRead: number;
  /** Grid cells the reader observed across the run. */
  cellsObserved: number;
  /** Distinct Pokédex cells the scanner resolved, caught or not. */
  cellsResolved: number;
  /** The Pokédex's own chrome (region, filter, progress) was seen at least once. */
  sawPokedex: boolean;
  /** A Pokémon detail page or a caught-Pokémon page was read at least once. */
  sawPokemonPage: boolean;
  /** Native OCR calls, and how many hit the native deadline or the JS fence. */
  ocrCalls: number;
  ocrTimeouts: number;
  /** "finish" = the trainer ended the run (or the app tore it down); "stop" = a restart. */
  endedBy: "finish" | "stop";
}

/** A run stopped inside this many ms was never given a chance. */
export const CANCELLED_UNDER_MS = 3000;

/**
 * The one word for a live run that wrote nothing. Checked most-certain first:
 * a run stopped in seconds says nothing about the scanner; timeouts beat
 * everything else because they also produce no text; "no text" means the
 * OCR saw nothing on ANY screen; "wrong screen" means it saw text but never
 * the Pokédex; resolved cells with nothing written is a working scanner on
 * a finished dex.
 */
export function classifyLiveScanOutcome(s: LiveScanSignals): ScanOutcome {
  if (s.wallMs < CANCELLED_UNDER_MS) return "cancelled";
  if (s.ocrTimeouts > 0 && s.ocrTimeouts * 2 >= s.ocrCalls) return "timeout";
  if (s.numbersRead === 0 && s.cellsObserved === 0) return "no_text";
  if (!s.sawPokedex && !s.sawPokemonPage) return "wrong_screen";
  if (s.cellsResolved > 0) return "nothing_new";
  if (s.endedBy === "stop") return "cancelled";
  return "unknown";
}

/** "Nothing readable 3 · Not on the Pokédex 1 · Older build, no reason 5", most first; "" when nothing failed. */
export function describeOutcomeCounts(counts: Record<string, number> | null | undefined): string {
  if (!counts) return "";
  return Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${SCAN_OUTCOME_SHORT[k as ScanOutcomeKey] ?? k} ${n}`)
    .join(" · ");
}
